import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { combineLatest, of } from 'rxjs';
import { catchError, debounceTime, delay, filter, groupBy, map, mapTo, mergeMap, skipUntil, switchMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { isEmpty } from 'lodash';
import { connectToNotificationChannelSuccess, notificationArrived } from '../../../notifications/actions/notification.actions';
import {
  acceptMessageConversation,
  closeMessageConversation,
  evictMessageConversation,
  initializeModule,
  loadMessageConversations,
  loadWrapUpsForMessageConversation,
  messageConversationChanged,
  messageConversationsLoaded,
  saveWrapUpForMessageConversation,
  skipWrapUpForMessageConversation,
  transferMessageConversationToUser,
  transferMessageConversationToQueue,
  transferMessageConversationFailure,
  transferMessageConversationSuccess,
  wrapUpsLoadedForMessageConversation
} from '../actions/message-conversation.actions';
import { selectCommunicationId, selectMessageConversationById, selectWrapUpsByConversationId } from '../selectors/message-conversation.selectors';
import { ConversationResponse } from '../../common/models/conversation-response.models';
import { selectUser } from '../../../users/selectors/user.selectors';
import { selectConversationsNotificationTopicName, selectConversationsTypingNotificationTopicName } from '../../common/selectors/conversation.selectors';
import { MessageConversationApiService } from '../services/message-conversation-api.service';
import { MessageConversationConverterService } from '../services/message-conversation-converter.service';
import { openConversationWorkItem } from '../../common/actions/conversation-work-item.actions';
import { WrapUpCode } from '../../common/models/wrap-up-code.model';
import { ParticipantPurpose } from '../../common/models/participant-purpose.enum';

@Injectable({
  providedIn: 'root'
})
export class GenesysCloudMessageConversationEffects {
  private readonly existingUser$ = this.store.select(selectUser).pipe(
    filter(user => !!user)
  );
  private readonly initializeModule$ = this.actions$.pipe(
    ofType(initializeModule)
  );
  private readonly _handleMessageConversationTypingNotifications$ = this.actions$.pipe(
    ofType(notificationArrived),
    concatLatestFrom(() => this.store.select(selectConversationsTypingNotificationTopicName)),
    filter(([{ notification }, topicName]) => notification.topicName === topicName),
    map(([{ notification }]) => notification.eventBody)
  );

  readonly init$ = createEffect(() => combineLatest(
    this.initializeModule$,
    this.existingUser$
  ).pipe(
    map(() => loadMessageConversations())
  )
  );

  readonly reloadConversationsOnReconnect$ = createEffect(() => this.actions$.pipe(
    skipUntil(this.initializeModule$),
    ofType(connectToNotificationChannelSuccess),
    filter(({ isReconnect }) => isReconnect),
    map(() => loadMessageConversations())
  ));

  readonly loadConversations$ = createEffect(() => this.actions$.pipe(
    ofType(loadMessageConversations),
    switchMap(() => this.api.getMessageConversations$()),
    concatLatestFrom(() => this.existingUser$),
    map(([conversations, agentUser]) =>
      // Filter out conversations that are not assigned to the current agent (eg monitoring only)
      conversations.filter(conversation => GenesysCloudMessageConversationEffects.isUserAssignedToConversationAsAgent(conversation, agentUser.id))
        .map(conversation => this.converter.convert(conversation, agentUser.id))
    ),
    map(extendedConversations => messageConversationsLoaded({ conversations: extendedConversations }))
  ));

  readonly handleMessageConversationNotifications$ = createEffect(() => this.actions$.pipe(
    ofType(notificationArrived),
    concatLatestFrom(() => this.store.select(selectConversationsNotificationTopicName)),
    filter(([{ notification }, topicName]) => notification.topicName === topicName),
    map(([{ notification }, _]) => <ConversationResponse>notification.eventBody),
    filter(GenesysCloudMessageConversationEffects.isMessageType),
    concatLatestFrom(() => this.existingUser$),
    filter(([eventBody, agentUser]) => GenesysCloudMessageConversationEffects.isUserAssignedToConversationAsAgent(eventBody, agentUser.id)),
    map(([eventBody, agentUser]) => this.converter.convert(eventBody, agentUser.id)),
    map(conversation => messageConversationChanged({ conversation }))
  ));

  readonly turnOnConversationTyping$ = createEffect(() =>
    this._handleMessageConversationTypingNotifications$.pipe(
      concatLatestFrom(({ conversationId }) => this.store.select(selectMessageConversationById(conversationId))),
      map(([_, conversation]) => messageConversationChanged({ conversation: { ...conversation, isCustomerTyping: true } }))
    ));

  readonly turnOffConversationTyping$ = createEffect(() =>
    this._handleMessageConversationTypingNotifications$.pipe(
      groupBy(({ conversationId }) => conversationId),
      mergeMap(group =>
        group.pipe(
          debounceTime(5000),
          concatLatestFrom(({ conversationId }) => this.store.select(selectMessageConversationById(conversationId))),
          map(([_, conversation]) =>
            messageConversationChanged({ conversation: { ...conversation, isCustomerTyping: false } })
          )
        )
      )
    )
  );

  readonly conversationAccept$ = createEffect(() => this.actions$.pipe(
    ofType(acceptMessageConversation),
    concatLatestFrom(({ conversationId }) => this.store.select(selectMessageConversationById(conversationId))),
    filter(([_, conversation]) => conversation.isAcceptable),
    mergeMap(([{ focus }, conversation]) => this.api.acceptConversation$(conversation.id, conversation.agentParticipant.id).pipe(
      mapTo(openConversationWorkItem({ conversationId: conversation.id, focus }))
    ))
  ));

  readonly conversationClose$ = createEffect(() => this.actions$.pipe(
    ofType(closeMessageConversation),
    concatLatestFrom(({ conversationId }) => this.store.select(selectMessageConversationById(conversationId))),
    mergeMap(([_, conversation]) => this.api.closeConversation$(conversation.id, conversation.agentParticipant.id))
  ), { dispatch: false });

  readonly loadWrapUpsForMessageConversation$ = createEffect(() => this.actions$.pipe(
    ofType(loadWrapUpsForMessageConversation),
    concatLatestFrom(({ conversationId }) => this.store.select(selectWrapUpsByConversationId(conversationId))),
    filter(([_, wrapUpCodes]) => !GenesysCloudMessageConversationEffects.areWrapUpCodesLoaded(wrapUpCodes)),
    concatLatestFrom(([{ conversationId }]) => this.store.select(selectMessageConversationById(conversationId))),
    mergeMap(([_, conversation]) => this.api.getWrapUpCodes$(conversation.id, conversation.agentParticipant.id).pipe(
      map(wrapUpCodes => wrapUpsLoadedForMessageConversation({ conversationId: conversation.id, wrapUpCodes }))
    ))
  ));

  readonly skipWrapUpForMessageConversation$ = createEffect(() => this.actions$.pipe(
    ofType(skipWrapUpForMessageConversation),
    concatLatestFrom(({ conversationId }) => [
      this.store.select(selectMessageConversationById(conversationId))
    ]),
    mergeMap(([_, conversation]) => this.api.skipWrapUpForConversation$(
      conversation.id,
      conversation.agentParticipant.id
    ))
  ), { dispatch: false });

  readonly saveWrapUpForMessageConversation$ = createEffect(() => this.actions$.pipe(
    ofType(saveWrapUpForMessageConversation),
    concatLatestFrom(({ conversationId }) => [
      this.store.select(selectMessageConversationById(conversationId)),
      this.store.select(selectCommunicationId(conversationId))
    ]),
    mergeMap(([action, conversation, communicationId]) => this.api.wrapUpConversation$(
      conversation.id,
      conversation.agentParticipant.id,
      communicationId,
      action.wrapUpCode
    ))
  ), { dispatch: false });

  readonly evictMessageConversation$ = createEffect(() => this.actions$.pipe(
    ofType(messageConversationChanged),
    filter(({ conversation }) => !conversation.isActive),
    map(({ conversation }) => evictMessageConversation({ conversationId: conversation.id })),
    delay(0)
  ));

  readonly transferMessageConversationToUser$ = createEffect(() => this.actions$.pipe(
    ofType(transferMessageConversationToUser),
    mergeMap(({ conversationId, participantId, userId }) => this.api.transferConversationToUser$(conversationId, participantId, userId).pipe(
      map(() => transferMessageConversationSuccess({ conversationId })),
      catchError(() => of(transferMessageConversationFailure({ conversationId })))
    ))
  ));

  readonly transferMessageConversationToQueue$ = createEffect(() => this.actions$.pipe(
    ofType(transferMessageConversationToQueue),
    mergeMap(({ conversationId, participantId, queueId }) => this.api.transferConversationToQueue$(conversationId, participantId, queueId).pipe(
      map(() => transferMessageConversationSuccess({ conversationId })),
      catchError(() => of(transferMessageConversationFailure({ conversationId })))
    ))
  ));

  constructor(private readonly actions$: Actions,
    private readonly api: MessageConversationApiService,
    private readonly converter: MessageConversationConverterService,
    private readonly store: Store
  ) {
  }

  private static isMessageType(conversationResponse: ConversationResponse): boolean {
    return conversationResponse?.participants.some(p => !isEmpty(p.messages));
  }

  private static areWrapUpCodesLoaded(wrapUpCodes: WrapUpCode[]): boolean {
    return wrapUpCodes?.length > 0;
  }

  private static isUserAssignedToConversationAsAgent(conversation: ConversationResponse, userId: string): boolean {
    return conversation.participants.some(participant => participant.userId === userId && participant.purpose === ParticipantPurpose.Agent);
  }
}
